// src/pages/ambulance/AmbulanceProfile.js
import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  Edit2,
  Save,
  X,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  Ambulance as AmbulanceIcon,
  Building,
  Settings,
  Users,
  Truck,
  Award,
  Star,
  Shield
} from 'lucide-react';

const AmbulanceProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isIndependent: true,
    contact: {
      phone: '',
      email: '',
      emergency: '',
      whatsapp: ''
    },
    location: {
      address: ''
    },
    pricing: {
      baseFee: 0,
      perKmRate: 0,
      waitingChargePerHour: 0,
      currency: 'KES'
    },
    operatingHours: {
      is24Hours: true,
      monday: { open: '00:00', close: '23:59', isClosed: false },
      tuesday: { open: '00:00', close: '23:59', isClosed: false },
      wednesday: { open: '00:00', close: '23:59', isClosed: false },
      thursday: { open: '00:00', close: '23:59', isClosed: false },
      friday: { open: '00:00', close: '23:59', isClosed: false },
      saturday: { open: '00:00', close: '23:59', isClosed: false },
      sunday: { open: '00:00', close: '23:59', isClosed: false }
    },
    services: [],
    capabilities: {
      basicLifeSupport: false,
      advancedLifeSupport: false,
      criticalCare: false,
      neonatalCare: false,
      bariatricTransport: false
    }
  });

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.ambulances.getProfile();
      
      if (response.success) {
        setProfile(response.data);
        setFormData({
          name: response.data.name || '',
          description: response.data.description || '',
          isIndependent: response.data.isIndependent !== false,
          contact: {
            phone: response.data.contact?.phone || '',
            email: response.data.contact?.email || '',
            emergency: response.data.contact?.emergency || '',
            whatsapp: response.data.contact?.whatsapp || ''
          },
          location: {
            address: response.data.location?.address || ''
          },
          pricing: {
            baseFee: response.data.pricing?.baseFee || 0,
            perKmRate: response.data.pricing?.perKmRate || 0,
            waitingChargePerHour: response.data.pricing?.waitingChargePerHour || 0,
            currency: response.data.pricing?.currency || 'KES'
          },
          operatingHours: response.data.operatingHours || formData.operatingHours,
          services: response.data.services || [],
          capabilities: response.data.capabilities || formData.capabilities
        });
      } else if (response.needsProfile) {
        setError('Profile not found. Please create your profile first.');
      }
    } catch (err) {
      console.error('Fetch profile error:', err);
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name?.trim()) {
      errors.name = 'Service name is required';
    }

    if (!formData.contact?.phone?.trim()) {
      errors.phone = 'Phone number is required';
    }

    if (formData.contact?.email && !isValidEmail(formData.contact.email)) {
      errors.email = 'Invalid email format';
    }

    if (formData.pricing?.baseFee < 0) {
      errors.baseFee = 'Base fee cannot be negative';
    }

    if (formData.pricing?.perKmRate < 0) {
      errors.perKmRate = 'Per km rate cannot be negative';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        const [parent, child] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: { ...prev[parent], [child]: type === 'checkbox' ? checked : value }
        }));
      } else if (parts.length === 3) {
        const [parent, middle, child] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [middle]: {
              ...prev[parent][middle],
              [child]: type === 'checkbox' ? checked : value
            }
          }
        }));
      }
    } else {
      setFormData(prev => ({ 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
      }));
    }

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCapabilityChange = (capability) => {
    setFormData(prev => ({
      ...prev,
      capabilities: {
        ...prev.capabilities,
        [capability]: !prev.capabilities[capability]
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please fix the validation errors');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const response = await apiService.ambulances.updateProfile(formData);
      
      if (response.success) {
        setSuccessMessage('Profile updated successfully!');
        setEditing(false);
        await fetchProfile();
      } else {
        setError(response.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Update profile error:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setValidationErrors({});
    setError(null);
    // Reset form to profile data
    if (profile) {
      setFormData({
        name: profile.name || '',
        description: profile.description || '',
        isIndependent: profile.isIndependent !== false,
        contact: profile.contact || formData.contact,
        location: profile.location || formData.location,
        pricing: profile.pricing || formData.pricing,
        operatingHours: profile.operatingHours || formData.operatingHours,
        services: profile.services || [],
        capabilities: profile.capabilities || formData.capabilities
      });
    }
  };

  const getVerificationStatusBadge = () => {
    if (!profile) return null;

    const statusConfig = {
      verified: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Verified' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Pending Verification' },
      rejected: { color: 'bg-red-100 text-red-800', icon: AlertCircle, text: 'Verification Rejected' }
    };

    const config = statusConfig[profile.verificationStatus] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${config.color}`}>
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{config.text}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!profile && !editing) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-yellow-900 mb-1">
              Profile Not Found
            </h3>
            <p className="text-yellow-700 mb-4">
              You need to create your ambulance profile first.
            </p>
            <button
              onClick={() => window.location.href = '/ambulance/setup'}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
            >
              Create Profile
            </button>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: AmbulanceIcon },
    { id: 'contact', label: 'Contact', icon: Phone },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'hours', label: 'Hours', icon: Clock },
    { id: 'capabilities', label: 'Capabilities', icon: Shield }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Ambulance Profile</h1>
          <p className="text-gray-600 mt-1">Manage your service information</p>
        </div>
        <div className="flex items-center gap-3">
          {getVerificationStatusBadge()}
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-800 font-medium">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {profile && !editing && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Vehicles</p>
                <p className="text-2xl font-bold text-gray-900">
                  {profile.limits?.currentVehicles || 0}/{profile.limits?.maxVehicles || 1}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Drivers</p>
                <p className="text-2xl font-bold text-gray-900">
                  {profile.limits?.currentDrivers || 0}/{profile.limits?.maxDrivers || 1}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Award className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Trips</p>
                <p className="text-2xl font-bold text-gray-900">
                  {profile.stats?.totalTrips || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Star className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Rating</p>
                <p className="text-2xl font-bold text-gray-900">
                  {profile.rating?.average?.toFixed(1) || '0.0'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Tabs */}
        {editing && (
          <div className="border-b border-gray-200">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-red-600 text-red-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="p-6">
          {editing ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info Tab */}
              {activeTab === 'basic' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        validationErrors.name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="e.g., City Emergency Ambulance Service"
                    />
                    {validationErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Describe your ambulance service, equipment, and areas covered..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service Type
                    </label>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="isIndependent"
                          checked={formData.isIndependent}
                          onChange={() => setFormData(prev => ({ ...prev, isIndependent: true }))}
                          className="text-red-600 focus:ring-red-500"
                        />
                        <span className="text-sm text-gray-700">Independent</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="isIndependent"
                          checked={!formData.isIndependent}
                          onChange={() => setFormData(prev => ({ ...prev, isIndependent: false }))}
                          className="text-red-600 focus:ring-red-500"
                        />
                        <span className="text-sm text-gray-700">Hospital-based</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service Address
                    </label>
                    <input
                      type="text"
                      name="location.address"
                      value={formData.location.address}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Enter your service location"
                    />
                  </div>
                </div>
              )}

              {/* Contact Tab */}
              {activeTab === 'contact' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Primary Phone *
                      </label>
                      <input
                        type="tel"
                        name="contact.phone"
                        value={formData.contact.phone}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                          validationErrors.phone ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="+254..."
                      />
                      {validationErrors.phone && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.phone}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Emergency Hotline
                      </label>
                      <input
                        type="tel"
                        name="contact.emergency"
                        value={formData.contact.emergency}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="+254..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        name="contact.email"
                        value={formData.contact.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                          validationErrors.email ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="contact@example.com"
                      />
                      {validationErrors.email && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        WhatsApp
                      </label>
                      <input
                        type="tel"
                        name="contact.whatsapp"
                        value={formData.contact.whatsapp}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="+254..."
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Pricing Tab */}
              {activeTab === 'pricing' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Base Fee (KES)
                      </label>
                      <input
                        type="number"
                        name="pricing.baseFee"
                        value={formData.pricing.baseFee}
                        onChange={handleInputChange}
                        min="0"
                        step="100"
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                          validationErrors.baseFee ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {validationErrors.baseFee && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.baseFee}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Per KM Rate (KES)
                      </label>
                      <input
                        type="number"
                        name="pricing.perKmRate"
                        value={formData.pricing.perKmRate}
                        onChange={handleInputChange}
                        min="0"
                        step="10"
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                          validationErrors.perKmRate ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {validationErrors.perKmRate && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.perKmRate}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Waiting Charge/Hour (KES)
                      </label>
                      <input
                        type="number"
                        name="pricing.waitingChargePerHour"
                        value={formData.pricing.waitingChargePerHour}
                        onChange={handleInputChange}
                        min="0"
                        step="100"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <Settings className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-900">Pricing Example</p>
                        <p className="text-sm text-blue-700 mt-1">
                          For a 10km trip: KES {formData.pricing.baseFee.toLocaleString()} (base) + 
                          KES {(formData.pricing.perKmRate * 10).toLocaleString()} (distance) = 
                          KES {(formData.pricing.baseFee + formData.pricing.perKmRate * 10).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Operating Hours Tab */}
              {activeTab === 'hours' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <input
                      type="checkbox"
                      name="operatingHours.is24Hours"
                      checked={formData.operatingHours.is24Hours}
                      onChange={handleInputChange}
                      className="text-red-600 focus:ring-red-500 rounded"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Open 24/7
                    </label>
                  </div>

                  {!formData.operatingHours.is24Hours && (
                    <div className="space-y-3">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                        <div key={day} className="grid grid-cols-4 gap-4 items-center">
                          <div className="font-medium text-gray-700 capitalize">{day}</div>
                          <input
                            type="time"
                            name={`operatingHours.${day}.open`}
                            value={formData.operatingHours[day]?.open || ''}
                            onChange={handleInputChange}
                            disabled={formData.operatingHours[day]?.isClosed}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100"
                          />
                          <input
                            type="time"
                            name={`operatingHours.${day}.close`}
                            value={formData.operatingHours[day]?.close || ''}
                            onChange={handleInputChange}
                            disabled={formData.operatingHours[day]?.isClosed}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100"
                          />
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              name={`operatingHours.${day}.isClosed`}
                              checked={formData.operatingHours[day]?.isClosed || false}
                              onChange={handleInputChange}
                              className="text-red-600 focus:ring-red-500 rounded"
                            />
                            <span className="text-sm text-gray-600">Closed</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Capabilities Tab */}
              {activeTab === 'capabilities' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Select the medical capabilities your ambulance service provides
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { key: 'basicLifeSupport', label: 'Basic Life Support (BLS)' },
                      { key: 'advancedLifeSupport', label: 'Advanced Life Support (ALS)' },
                      { key: 'criticalCare', label: 'Critical Care Transport' },
                      { key: 'neonatalCare', label: 'Neonatal Care' },
                      { key: 'bariatricTransport', label: 'Bariatric Transport' }
                    ].map((capability) => (
                      <label
                        key={capability.key}
                        className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.capabilities[capability.key]}
                          onChange={() => handleCapabilityChange(capability.key)}
                          className="text-red-600 focus:ring-red-500 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          {capability.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex items-center gap-3 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <LoadingSpinner size="small" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            /* View Mode */
            <div className="space-y-6">
              {/* Basic Info Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <AmbulanceIcon className="w-5 h-5 text-red-600" />
                  Basic Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Service Name</p>
                    <p className="text-lg font-semibold text-gray-900">{profile.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Description</p>
                    <p className="text-gray-900">
                      {profile.description || 'No description provided'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Service Type</p>
                    <div className="flex items-center gap-2 mt-1">
                      {profile.isIndependent ? (
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                          Independent Service
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full flex items-center gap-1">
                          <Building className="w-3 h-3" />
                          Hospital-based
                        </span>
                      )}
                    </div>
                  </div>
                  {profile.location?.address && (
                    <div>
                      <p className="text-sm text-gray-600">Address</p>
                      <p className="text-gray-900 flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                        {profile.location.address}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Section */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-red-600" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Primary Phone</p>
                    <p className="font-medium text-gray-900 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      {profile.contact?.phone || 'N/A'}
                    </p>
                  </div>
                  {profile.contact?.emergency && (
                    <div>
                      <p className="text-sm text-gray-600">Emergency Hotline</p>
                      <p className="font-medium text-red-600 flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        {profile.contact.emergency}
                      </p>
                    </div>
                  )}
                  {profile.contact?.email && (
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium text-gray-900 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        {profile.contact.email}
                      </p>
                    </div>
                  )}
                  {profile.contact?.whatsapp && (
                    <div>
                      <p className="text-sm text-gray-600">WhatsApp</p>
                      <p className="font-medium text-gray-900">{profile.contact.whatsapp}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pricing Section */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-red-600" />
                  Pricing
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Base Fee</p>
                    <p className="text-2xl font-bold text-gray-900">
                      KES {profile.pricing?.baseFee?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Per KM Rate</p>
                    <p className="text-2xl font-bold text-gray-900">
                      KES {profile.pricing?.perKmRate?.toLocaleString() || '0'}
                    </p>
                  </div>
                  {profile.pricing?.waitingChargePerHour > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Waiting Charge/Hour</p>
                      <p className="text-2xl font-bold text-gray-900">
                        KES {profile.pricing.waitingChargePerHour.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Operating Hours Section */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-red-600" />
                  Operating Hours
                </h3>
                {profile.operatingHours?.is24Hours ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 font-medium flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Open 24 Hours, 7 Days a Week
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                      const hours = profile.operatingHours?.[day];
                      return (
                        <div key={day} className="flex items-center justify-between py-2 border-b border-gray-100">
                          <span className="font-medium text-gray-700 capitalize w-32">{day}</span>
                          {hours?.isClosed ? (
                            <span className="text-red-600 font-medium">Closed</span>
                          ) : (
                            <span className="text-gray-900">
                              {hours?.open || '00:00'} - {hours?.close || '23:59'}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Capabilities Section */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-red-600" />
                  Medical Capabilities
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { key: 'basicLifeSupport', label: 'Basic Life Support (BLS)' },
                    { key: 'advancedLifeSupport', label: 'Advanced Life Support (ALS)' },
                    { key: 'criticalCare', label: 'Critical Care Transport' },
                    { key: 'neonatalCare', label: 'Neonatal Care' },
                    { key: 'bariatricTransport', label: 'Bariatric Transport' }
                  ].map((capability) => (
                    <div
                      key={capability.key}
                      className={`flex items-center gap-2 p-3 rounded-lg border ${
                        profile.capabilities?.[capability.key]
                          ? 'bg-green-50 border-green-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {profile.capabilities?.[capability.key] ? (
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <X className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      )}
                      <span
                        className={`text-sm font-medium ${
                          profile.capabilities?.[capability.key]
                            ? 'text-green-900'
                            : 'text-gray-500'
                        }`}
                      >
                        {capability.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Subscription Info */}
              {profile.subscription && (
                <div className="pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-red-600" />
                    Subscription Plan
                  </h3>
                  <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-2xl font-bold text-gray-900 capitalize">
                          {profile.subscription.plan} Plan
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          {profile.subscription.status === 'active' ? 'Active' : profile.subscription.status}
                        </p>
                      </div>
                      <button
                        onClick={() => window.location.href = '/ambulance/subscription'}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Manage Subscription
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <p className="text-sm text-gray-600">Max Drivers</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {profile.limits?.maxDrivers || 1}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Max Vehicles</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {profile.limits?.maxVehicles || 1}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AmbulanceProfile;